import React, { useState, useEffect, useRef } from 'react';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect, useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import Header from '~/pages/Manager/Header';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';

import {
  getOperatorsGroupsTemplate,
  importOperatorsGroups,
  getOperatorsGroups,
  resetFileUploadErrors,
  addOperatorsGroup,
  setOperatorsGroupsValues,
  resetOperatorsGroups,
  updateOperatorsGroups,
} from '~/store/manager/actions';
import PF2MDataTable from '~/components/PF2MDataTable';
import { checkDuplicated } from '~/utils';

const GroupsTab = ({
  classes,
  operatorsGroups,
  getOperatorsGroupsTemplate: getTemplateAction,
  importOperatorsGroups: importAction,
  getOperatorsGroups: getAll,
  addOperatorsGroup: addItem,
  setOperatorsGroupsValues: updateItem,
  resetOperatorsGroups: reset,
  updateOperatorsGroups: updateOperators,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [showDialogImport, setShowDialogImport] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const fileUploadErrors = useSelector(state => state.manager.fileUploadErrors);

  const tableRef = useRef();

  useEffect(() => {
    async function fetchOperators() {
      await getAll(true);
    }
    if (!groupsLoaded) {
      fetchOperators();
      setGroupsLoaded(true);
    }
  }, [groupsLoaded, getAll]);

  useEffect(() => {
    if (fileUploadErrors && fileUploadErrors.length > 0) {
      const message = fileUploadErrors.map((error) => {
        const [rowCol, description] = error.split('-');
        const [row, col] = rowCol.split(';');
        return `${translate('validation:Row')} ${row} ${translate('validation:Col')} ${col} - ${translate(`validation:${description}`)}`;
      });
      setDialogMessage(message.join('\r\n'));
      setShowDialogImport(true);
    }
  }, [fileUploadErrors]);

  const columns = [
    {
      editable: 'always',
      title: translate('common:Name'),
      field: 'name',
      // unique: true,
      required: true,
      validate: ({ name, tableData }) => {
        const regex = /^[\p{L}\p{N}\s\-_|+()'º./]+$/u;

        if (!name || String(name).trim() === '') {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }

        if (name.length > 64) {
          return { isValid: false, helperText: translate('validation:maxLength64') };
        }

        if (!regex.test(name)) {
          return { isValid: false, helperText: translate('validation:validChars') };
        }

        const duplicated = checkDuplicated(tableData, 'name', name, tableRef);

        if (duplicated) {
          return {
            isValid: false,
            helperText: translate('validation:DuplicatedRegister'),
          };
        }

        return { isValid: true };
      },
    },
    {
      editable: 'always',
      field: 'description',
      title: translate('common:Description'),
      required: true,
      validate: ({ description }) => {
        if (!description || String(description).trim() === '') {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }

        if (description.length > 64) {
          return { isValid: false, helperText: translate('validation:maxLength64') };
        }

        return { isValid: true };
      },
    },
    {
      editable: 'always',
      title: translate('common:STATUS'),
      field: 'active',
      customType: 'boolean',
      lookupKey: 'value',
      lookup: [
        { name: translate('common:Inactive'), value: 0 },
        { name: translate('common:Active'), value: 1 },
      ],
      defaultFilter: isEditing ? undefined : ['1'],
      required: true,
      validate: ({ active }) => {
        if (active === null || active === undefined) {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }

        return { isValid: true };
      },

    },
  ];

  return (
    <div className={classes.tabContainer}>
      <Header
        getTemplateAction={getTemplateAction}
        importAction={importAction}
      />
      <PF2MDataTable
        ref={tableRef}
        title={translate('common:Group')}
        options={{
          search: true,
          selection: true,
          filtering: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          hideDelete: true,
          editableRowValidation: rowData => (rowData.id < 0 && rowData.update_status !== 'I'),
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 350px)',
        }}
        columns={columns}
        data={operatorsGroups}
        onAdd={newData => addItem(newData)}
        onChange={(newData) => {
          setIsEditing(true);
          newData.forEach((row) => {
            updateItem({
              ...row,
              active: row.active,
              name: row.name.trim(),
            });
          });
        }}
        onDiscard={() => {
          reset();
        }}
        onApply={() => {
          const hasPendingChanges = operatorsGroups.some(row => row.update_status);
          if (!hasPendingChanges) return;
          updateOperators(operatorsGroups);
        }}
      />
      <PF2MAlertDialog
        title={translate('validation:ImportErrors')}
        description={dialogMessage}
        open={showDialogImport}
        onConfirm={() => {
          setShowDialogImport(false);
          dispatch(resetFileUploadErrors());
        }}
        onClose={() => setShowDialogImport(false)}
        hasCancel={false}
      />
    </div>
  );
};

GroupsTab.propTypes = {
  classes: PropTypes.object.isRequired,
  operatorsGroups: PropTypes.array,
  getOperatorsGroupsTemplate: PropTypes.func,
  importOperatorsGroups: PropTypes.func,
  getOperatorsGroups: PropTypes.func,
  addOperatorsGroup: PropTypes.func,
  setOperatorsGroupsValues: PropTypes.func,
  resetOperatorsGroups: PropTypes.func,
  updateOperatorsGroups: PropTypes.func,
};

GroupsTab.defaultProps = {
  operatorsGroups: [],
  getOperatorsGroupsTemplate: () => {},
  importOperatorsGroups: () => {},
  getOperatorsGroups: () => {},
  addOperatorsGroup: () => {},
  setOperatorsGroupsValues: () => {},
  resetOperatorsGroups: () => {},
  updateOperatorsGroups: () => {},
};

const mapStateToProps = state => ({
  operatorsGroups: state.manager.operatorsGroups,
});

const mapDispatchToProps = {
  getOperatorsGroupsTemplate,
  importOperatorsGroups,
  getOperatorsGroups,
  addOperatorsGroup,
  setOperatorsGroupsValues,
  resetOperatorsGroups,
  updateOperatorsGroups,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(GroupsTab));
